/***************/
/**  IMPORTS  **/
/***************/
import { QueryClient, QueryClientProvider, useQuery } from 'react-query';
import Cookies from "universal-cookie";
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast, Toaster, ToastBar } from 'react-hot-toast';
import { Button } from 'react-bootstrap';

/***********************/
/**  AMPLIFY IMPORTS  **/
/***********************/
import { Amplify } from 'aws-amplify';
import amplifyconfig from '../amplifyconfiguration.json';
import { get } from 'aws-amplify/api';

/****************************/
/**  LOCAL/ASSETS IMPORTS  **/
/****************************/
import Navbar from "../components/Navbar";
import Dropdown from "../components/Dropdown";
import PDFOptionsModal from '../components/PDFOptionsModal';
import { LocationProvider } from "../utils/LocationsContext";
import { abbreviateStreetName } from '../utils/LocationsContext';
import { StreetName, MyCol, PDFDownload } from "../styles/MapPageStyles";


/**
 * This component is responsible for displaying the Map Page
 * @returns The Map Page
 */
export default function MapPage() {

  let queryClient = new QueryClient();
  Amplify.configure(amplifyconfig);

  const cookies = new Cookies();
  const navigate = useNavigate();
  const { street } = useParams();
  const [streetname, setStreetname] = useState(street);
  const [groupedAddresses, setGroupedAddresses] = useState({});
  const [flipped, setFlipped] = useState(false);
  const [openPDFSettings, setOpenPDFSettings] = useState(false);
  const [modalChange, setModalChange] = useState(false);
  const [allLocations, setAllLocations] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  useEffect(() => { 
    if(!street) {
      setStreetname("Union Street");
    } else {
      setStreetname(street);
    }
  })

  // Get token
  const token = cookies.get("TOKEN");
  /**  CHECK IF USER IS LOGGED IN/AUTHORIZED  **/
  useEffect(() => {
    if (!token) {
      navigate("/");
    }
  }, [token])

  // Get all location data for PDF
  const addresses = useQuery('data', () => {

      async function getData() {
          try {
              const theStreet = abbreviateStreetName(street);
              // Create a get request for all location data
              const data = get({
                  apiName: 'LocationAPI',
                  path: `/location/all-data?streetname=${encodeURIComponent(theStreet)}`,
                  options: {
                      headers: {
                          Authorization: `Bearer ${token}`
                      }
                  }
              })
              const { body } = await data.response;
              const res = await body.json();
              console.log('Getting all location data GET call succeeded for PDF');
              return res;
          } catch (e) {
              console.log('Getting all location data GET call failed for PDF: ', e);
          }
      }
      if(token) {
          return getData();
      }
  })

  async function groupTheAddresses(){
    const addressList = Array.isArray(addresses?.data?.addresses) ? addresses?.data?.addresses : [];
      const grouped = {};
      const allNumbers = [];
  
      addressList.forEach(addressObj => {
          const buildingNumber = parseInt(addressObj.address.split(" ")[0], 10);
          allNumbers.push(buildingNumber);
          const hundredGroup = Math.floor(buildingNumber / 100) * 100; // Get the hundreds group
          const isEven = buildingNumber % 2 === 0;
          const groupKey = isEven ? "evens" : "odds";
  
          if (!grouped[hundredGroup]) {
              grouped[hundredGroup] = { odds: [], evens: [] };
          }
  
          grouped[hundredGroup][groupKey].push(addressObj);
      });
  
      if(flipped){
        // Sort descending addresses within each group
        Object.keys(grouped).forEach(hundred => {
          grouped[hundred].odds.sort((a, b) => {
              const numA = parseInt(a.address.split(" ")[0], 10);
              const numB = parseInt(b.address.split(" ")[0], 10);
              return numB - numA;
          });
  
          grouped[hundred].evens.sort((a, b) => {
              const numA = parseInt(a.address.split(" ")[0], 10);
              const numB = parseInt(b.address.split(" ")[0], 10);
              return numB - numA;
          });
      });
      } else {
        // Sort ascending addresses within each group
        Object.keys(grouped).forEach(hundred => {
          grouped[hundred].odds.sort((a, b) => {
              const numA = parseInt(a.address.split(" ")[0], 10);
              const numB = parseInt(b.address.split(" ")[0], 10);
              return numA - numB; 
          });

          grouped[hundred].evens.sort((a, b) => {
              const numA = parseInt(a.address.split(" ")[0], 10);
              const numB = parseInt(b.address.split(" ")[0], 10);
              return numA - numB;
          });
        });
      }
      

      if (allNumbers.length > 0) {
        const minGroup = Math.floor(Math.min(...allNumbers) / 100) * 100;
        const maxGroup = Math.floor(Math.max(...allNumbers) / 100) * 100;

        for (let group = minGroup; group <= maxGroup; group += 100) {
            if (!grouped[group]) {
                grouped[group] = { odds: [], evens: [] };
            }
        }
      }
  
      setGroupedAddresses(grouped);
  }

  useEffect(() => {
      groupTheAddresses();
  }, [addresses?.data?.addresses]);

  const PDFhandler = (e) => {
    e.stopPropagation();
    setOpenPDFSettings(true);
  }

  const pdfProps = {
    street,
    groupedAddresses,
    flipped,
    setFlipped,
    addresses
  }

  return (
    <>
    <Navbar />
    <StreetName>
        <MyCol xs={12} md="auto">
          <Dropdown addresses={addresses} setGroupedAddresses={setGroupedAddresses} streetname={streetname} token={token} setAllLocations={setAllLocations}/>
        </MyCol>
        <MyCol xs={12} md="auto">{streetname}</MyCol>
        <MyCol xs={12} md="auto">
        <PDFDownload onClick={(e) => PDFhandler(e)}>Download PDF</PDFDownload>
        {openPDFSettings 
          && <PDFOptionsModal 
              groupedAddresses={groupedAddresses} 
              setGroupedAddresses={setGroupedAddresses}
              addresses={addresses}
              streetname={streetname} 
              setOpenPDFSettings={setOpenPDFSettings} 
              openPDFSettings={openPDFSettings} 
              queryClient={queryClient}
              pdfProps={pdfProps}
              street={street}
              flipped={flipped}
              token={token}
              setAllLocations={setAllLocations}/>}
        </MyCol>
    </StreetName>
    <Toaster 
      position='top-right'
      toastOptions={{
        className: '',
        style: {
          padding: '16px',
          color: '#205DBC',
          width: '400px',
        },
      }}>{(t) => (
        <ToastBar toast={t}>
          {({ icon, message }) => (
            <>
              {icon}
              {message}
              {t.type !== 'loading' && (
                <button 
                onClick={() => toast.dismiss(t.id)}
                style={{border: "none", backgroundColor: "white", color: "#205DBC"}}>
                  X
                </button>
              )}
            </>
          )}
        </ToastBar>
      )}</Toaster>
      <LocationProvider
        flipped={flipped}
        setFlipped={setFlipped}
        streetname={streetname}
        token={token}
        modalChange={modalChange}
        setModalChange={setModalChange}
        allLocations={allLocations}
        setAllLocations={setAllLocations}
        dataLoading={dataLoading}
        setDataLoading={setDataLoading}
        theAddresses={groupedAddresses}/>
    </>
  );
}